<template>
  <v-dialog
    :value="value"
    max-width="1100px"
    content-class="riw ras-hx-dialog"
    @input="$emit('input', $event)">
    <div class="ras-hx">
      <v-btn icon small class="ras-hx__close" aria-label="Close execution history" @click="close">
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>

      <header class="ras-hx__head">
        <h2 :id="`${_uid}-title`" class="ras-hx__title">Execution history</h2>
        <p class="ras-hx__sub">{{ dataSource ? dataSource.name : '' }}</p>
        <div v-if="dataSource" class="ras-hx__meta">
          <span v-if="register"><v-icon size="15">mdi-table-large</v-icon>{{ register.name }} · {{ ownerText }}</span>
          <span><v-icon size="15">mdi-calendar-clock</v-icon><b>{{ header.schedule }}</b></span>
          <span v-if="header.nextRun"><v-icon size="15">mdi-timer-sand</v-icon>Next run <b>{{ header.nextRun }}</b></span>
          <span v-if="!registerUnset" :class="{ 'is-missing': !header.identity }">
            <v-icon size="15">{{ header.identity ? 'mdi-fingerprint' : 'mdi-fingerprint-off' }}</v-icon>Identity <b>{{ header.identity || 'not mapped' }}</b>
          </span>
        </div>
      </header>

      <div v-if="registerUnset || (header.identityCallout && !registerUnset)" class="ras-hx__callouts riw-callout-stack">
        <div v-if="header.identityCallout && !registerUnset" class="riw-callout riw-callout--warn" role="status">
          <v-icon class="riw-callout__icon">mdi-fingerprint-off</v-icon>
          <div class="riw-callout__body"><span class="riw-callout__title">{{ header.identityCallout.title }}</span>{{ header.identityCallout.text }}</div>
          <div class="riw-callout__actions">
            <v-btn small outlined class="riw-callout__btn" @click="editSource">
              <v-icon left small>mdi-pencil-outline</v-icon>Edit mapping
            </v-btn>
          </div>
        </div>
        <div v-if="registerUnset" class="riw-callout riw-callout--err" role="status">
          <v-icon class="riw-callout__icon">mdi-lock-alert-outline</v-icon>
          <div class="riw-callout__body">
            <span class="riw-callout__title">Pulls are refused.</span>{{ register.name }} has {{ fmtCount(registerRowCount) }} rows and no Shared or Separated mode. An administrator must set the mode before this source can import.
          </div>
        </div>
      </div>

      <div class="ras-hx__card">
        <div class="ras-hx__toolbar">
          <span class="ras-hx__count"><b>{{ fmtCount(total) }}</b>{{ total === 1 ? 'run' : 'runs' }} · newest first</span>
          <v-progress-circular v-if="loading" indeterminate size="16" width="2" color="primary" aria-label="Loading runs" />
          <span class="ras-hx__spacer"></span>
          <span v-if="pageCount > 1" class="ras-hx__pager">
            <span class="riw-num">{{ rangeText }}</span>
            <v-btn icon small :disabled="page <= 1 || loading" aria-label="Newer runs" @click="goPage(page - 1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <v-btn icon small :disabled="page >= pageCount || loading" aria-label="Older runs" @click="goPage(page + 1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
          </span>
        </div>

        <div class="ras-hx__scroll" tabindex="0" aria-label="Execution runs, scrollable">
          <table class="ras-hx__table">
            <caption class="riw-sr-only">Pull runs for {{ dataSource ? dataSource.name : 'this source' }}, newest first</caption>
            <thead>
              <tr>
                <th class="is-sticky is-sorted" scope="col" aria-sort="descending">Started <v-icon size="14">mdi-arrow-down</v-icon></th>
                <th scope="col">Trigger</th>
                <th scope="col">Status</th>
                <th class="is-num" scope="col">Fetched</th>
                <th class="is-num" scope="col" title="Rows new to the register (imported minus updated)">Added</th>
                <th class="is-num" scope="col">Updated</th>
                <th class="is-num" scope="col">Skipped</th>
                <th class="is-num" scope="col">Duration</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in rows">
                <tr :key="row.id" class="ras-hx__run" :class="{ 'is-open': row.open }">
                  <td class="is-sticky">
                    <span class="ras-hx__when">
                      <button
                        v-if="row.expandable"
                        type="button"
                        class="ras-hx__exp"
                        :aria-expanded="String(row.open)"
                        :aria-controls="row.detailId"
                        :aria-label="`${row.open ? 'Hide' : 'Show'} details for the run started ${row.day} ${row.time}`"
                        @click="toggle(row.id)">
                        <v-icon size="18">mdi-chevron-right</v-icon>
                      </button>
                      <span v-else class="ras-hx__exp-spacer" aria-hidden="true"></span>
                      <span class="ras-hx__day">{{ row.day }}</span>
                      <span class="ras-hx__time riw-num">{{ row.time }}</span>
                    </span>
                  </td>
                  <td><span class="ras-hx__trig"><v-icon size="15">{{ row.trigger.icon }}</v-icon>{{ row.trigger.label }}</span></td>
                  <td>
                    <span class="ras-hx__status" :class="`is-${row.status.tone}`">
                      <v-icon size="16">{{ row.status.icon }}</v-icon>{{ row.status.label }}
                    </span>
                  </td>
                  <td class="is-num"><span v-if="row.counts.fetched === null" class="riw-dash">—</span><template v-else>{{ fmtCount(row.counts.fetched) }}</template></td>
                  <td class="is-num"><span v-if="row.counts.added === null" class="riw-dash">—</span><template v-else>{{ fmtCount(row.counts.added) }}</template></td>
                  <td class="is-num"><span v-if="row.counts.updated === null" class="riw-dash">—</span><template v-else>{{ fmtCount(row.counts.updated) }}</template></td>
                  <td class="is-num">
                    <span v-if="row.counts.skipped === null" class="riw-dash">—</span>
                    <span v-else :class="{ 'ras-hx__skip': row.counts.skipped > 0 }">{{ fmtCount(row.counts.skipped) }}</span>
                  </td>
                  <td class="is-num"><span v-if="!row.duration" class="riw-dash">—</span><template v-else>{{ row.duration }}</template></td>
                </tr>

                <tr v-if="row.open" :id="row.detailId" :key="`d-${row.id}`" class="ras-hx__detail">
                  <td colspan="8">
                    <div class="ras-hx__detail-in">
                      <div v-if="row.failure" class="ras-hx__fail" role="note">
                        <v-icon size="19">mdi-alert-circle-outline</v-icon>
                        <b>{{ row.failure }}</b>
                      </div>
                      <dl v-if="row.facts.length" class="ras-hx__facts">
                        <div v-for="fact in row.facts" :key="fact.label"><dt>{{ fact.label }}</dt><dd>{{ fact.value }}</dd></div>
                      </dl>
                      <div v-if="row.groups.length" class="ras-hx__groups">
                        <div v-for="group in row.groups" :key="group.key" class="ras-hx__group">
                          <div class="ras-hx__group-head">
                            <v-icon size="17">{{ group.icon }}</v-icon>
                            <span class="ras-hx__g-label">{{ group.label }}</span>
                            <span class="ras-hx__g-count riw-num">{{ fmtCount(group.count) }}</span>
                            <span v-if="group.outcome" class="riw-pill" :class="`riw-pill--${group.outcome.tone}`">{{ group.outcome.text }}</span>
                          </div>
                          <ul :id="group.listId" class="ras-hx__g-list">
                            <li v-for="(item, index) in group.visible" :key="`${group.key}-${index}`">
                              <span class="ras-hx__g-row riw-num">{{ item.row !== undefined && item.row !== null ? `Row ${fmtCount(item.row)}` : '—' }}</span>
                              <span>{{ item.message || '—' }}</span>
                            </li>
                          </ul>
                          <div v-if="group.count > GROUP_FIRST_N" class="ras-hx__g-more">
                            <button
                              type="button"
                              class="ras-link"
                              :aria-expanded="String(group.all)"
                              :aria-controls="group.listId"
                              @click="toggleMore(group.moreKey)">{{ group.all ? `Show first ${GROUP_FIRST_N}` : `Show all ${fmtCount(group.count)}` }}</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>

              <tr v-if="!rows.length">
                <td colspan="8" class="ras-hx__empty">
                  <template v-if="loading">Loading runs…</template>
                  <template v-else>
                    <v-icon size="36">mdi-history</v-icon>
                    <div>No runs recorded yet.</div>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <footer class="ras-hx__foot">
        <span class="riw-foot-hint"><v-icon>mdi-information-outline</v-icon>Rows skipped by a run are listed in its details</span>
        <span class="riw-foot-spacer"></span>
        <v-btn small outlined color="primary" :loading="running" :disabled="!dataSource || registerUnset" @click="runNow">
          <v-icon left small>mdi-play</v-icon>Run now
        </v-btn>
        <v-btn text @click="close">Close</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { log, error } from '@/utils/debugLogger';
  import apiDataSourceService from '../../services/apiDataSourceService';
  import registerService from '../../services/registerService';
  import { REGISTER_KIND, isGroupRegister, resolveRegisterKind } from '../../modules/registerImportWizardModel';
  import { loadUnsetRegisterRowCount, needsRowCount } from './apiSource/registerRowCount';
  import {
    GROUP_FIRST_N,
    dayLabel,
    durationLabel,
    failureMessage,
    fmtCount,
    groupOutcome,
    groupWarnings,
    historyHeader,
    isExpandable,
    runCounts,
    runFacts,
    statusPresentation,
    timeLabel,
    triggerPresentation,
  } from './apiSource/executionHistoryPresentation';
  import './importWizard/styles';
  import './apiSource/styles';

  /** Runs per page (server-paged: GET api/.../{id}/history?page&itemsPerPage). */
  const PAGE_SIZE = 25;

  /**
   * Execution history of a Register API data source (AVA-602 showcase D, plan N29): a dialog in the
   * WizardShell family (same frame as the shipped shell) with a DATA_TABLE_STANDARD run table (fixed
   * 48px rows — no density switch).
   * Expanding a run shows its counts and the rows it skipped, grouped by the API's `reasonCode`
   * (warnings without one are listed under "Other"; the message text is never parsed).
   */
  export default {
    name: 'ApiExecutionHistory',
    props: {
      value: { type: Boolean, default: false },
      dataSource: { type: Object, default: null },
      /** The source's register (owner, mode) — optional; the header omits what it can't show. */
      register: { type: Object, default: null },
    },
    data() {
      return {
        GROUP_FIRST_N,
        page: 1,
        openRuns: {},
        showAll: {},
        running: false,
        registerRowCount: null,
        rowCountGeneration: 0,
        now: new Date(),
      };
    },
    computed: {
      ...mapGetters('REGISTERS', {
        history: 'GET_EXECUTION_HISTORY',
        total: 'GET_EXECUTION_HISTORY_TOTAL',
        loading: 'GET_EXECUTION_HISTORY_LOADING',
      }),
      runs() {
        return Array.isArray(this.history) ? this.history : [];
      },
      pageCount() {
        return Math.max(1, Math.ceil((this.total || 0) / PAGE_SIZE));
      },
      rangeText() {
        const first = (this.page - 1) * PAGE_SIZE + 1;
        const last = Math.min(this.page * PAGE_SIZE, this.total || 0);
        return `${fmtCount(first)}–${fmtCount(last)} of ${fmtCount(this.total)}`;
      },
      registerUnset() {
        return !!this.register && resolveRegisterKind(this.register, this.registerRowCount) === REGISTER_KIND.GROUP_UNSET;
      },
      ownerText() {
        const reg = this.register || {};
        if (isGroupRegister(reg)) return `Facility Group ${(reg.facilityGroup && reg.facilityGroup.name) || ''}`.trim();
        return `Facility ${(reg.facility && reg.facility.name) || ''}`.trim();
      },
      header() {
        return historyHeader({ dataSource: this.dataSource, runs: this.runs, now: this.now });
      },
      rows() {
        return this.runs.map((run, index) => {
          const id = run.id || `run-${index}`;
          const open = isExpandable(run) && !!this.openRuns[id];
          return {
            id,
            open,
            expandable: isExpandable(run),
            detailId: `${this._uid}-d-${id}`,
            day: dayLabel(run.startedAt, this.now),
            time: timeLabel(run.startedAt),
            trigger: triggerPresentation(run.triggerType),
            status: statusPresentation(run.status),
            counts: runCounts(run),
            duration: durationLabel(run),
            failure: open ? failureMessage(run) : '',
            facts: open ? runFacts(run) : [],
            groups: open ? this.groupsFor(id, run) : [],
          };
        });
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(open) {
          if (!open) return;
          this.page = 1;
          this.openRuns = {};
          this.showAll = {};
          this.now = new Date();
          this.fetch();
          this.loadRegisterRowCount();
        },
      },
    },
    methods: {
      fmtCount,
      groupsFor(id, run) {
        return groupWarnings(run.warnings).map((group) => {
          const moreKey = `${id}:${group.key}`;
          const all = !!this.showAll[moreKey];
          return {
            ...group,
            moreKey,
            all,
            listId: `${this._uid}-g-${id}-${group.key}`,
            outcome: groupOutcome(group),
            visible: all ? group.items : group.items.slice(0, GROUP_FIRST_N),
          };
        });
      },
      fetch() {
        if (!this.dataSource) return;
        this.$store.dispatch('REGISTERS/FETCH_EXECUTION_HISTORY', {
          dataSourceId: this.dataSource.id,
          params: { page: this.page, itemsPerPage: PAGE_SIZE },
        }).catch((err) => {
          error('[RegisterApiSource] execution history failed to load', { dataSourceId: this.dataSource.id }, err);
          this.$dialog.notify.error('Execution history could not be loaded');
        });
      },
      goPage(page) {
        if (page < 1 || page > this.pageCount) return;
        this.page = page;
        this.openRuns = {};
        this.showAll = {};
        this.fetch();
      },
      toggle(id) {
        this.openRuns = { ...this.openRuns, [id]: !this.openRuns[id] };
      },
      toggleMore(key) {
        this.showAll = { ...this.showAll, [key]: !this.showAll[key] };
      },
      async loadRegisterRowCount() {
        const generation = ++this.rowCountGeneration;
        this.registerRowCount = null;
        if (!needsRowCount(this.register)) return;
        try {
          const rows = await loadUnsetRegisterRowCount(this.register, registerService);
          if (generation === this.rowCountGeneration) this.registerRowCount = rows;
        } catch (err) {
          if (generation === this.rowCountGeneration) {
            error('[RegisterApiSource] register row count failed to load for the history header', { registerId: this.register.id }, err);
          }
        }
      },
      async runNow() {
        if (!this.dataSource || this.running) return;
        this.running = true;
        try {
          const result = await apiDataSourceService.triggerManualPull(this.dataSource.id);
          log('[RegisterApiSource] manual pull triggered from history', { dataSourceId: this.dataSource.id, result });
          if (result && result.success === false) {
            this.$dialog.notify.error(result.message || 'The pull could not be started');
          } else {
            this.$dialog.notify.success((result && result.message) || 'Pull started');
          }
        } catch (err) {
          error('[RegisterApiSource] manual pull refused', { dataSourceId: this.dataSource.id }, err);
          this.$dialog.notify.error((err && err.response && err.response.data && err.response.data.message) || 'The pull could not be started');
        } finally {
          this.running = false;
          this.now = new Date();
          this.page = 1;
          this.fetch();
        }
      },
      editSource() {
        this.$emit('edit', this.dataSource);
      },
      close() {
        this.$emit('input', false);
      },
    },
  };
</script>
